/**
 * Estimate List Component
 * BuilderBel Construction Project Management System
 *
 * Grid/list display of estimate cards
 */

import React from 'react';
import { FileSpreadsheet } from 'lucide-react';
import type { Estimate } from '../../types/estimate';
import EstimateCard from './EstimateCard';

export interface EstimateListProps {
  estimates: Estimate[];
  onSelect: (estimate: Estimate) => void;
  onEdit?: (estimate: Estimate) => void;
  onSubmitForReview?: (estimate: Estimate) => void;
  onApprove?: (estimate: Estimate) => void;
  onReject?: (estimate: Estimate) => void;
  onCreateVersion?: (estimate: Estimate) => void;
  onDelete?: (estimate: Estimate) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

const LoadingSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
        <div className="h-5 w-48 bg-gray-200 rounded" />
      </div>
      <div className="h-6 w-20 bg-gray-200 rounded-full" />
    </div>
    <div className="h-4 w-full bg-gray-200 rounded mb-2" />
    <div className="h-4 w-3/4 bg-gray-200 rounded mb-4" />
    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
      {[1, 2, 3].map(i => (
        <div key={i}>
          <div className="h-3 w-16 bg-gray-200 rounded mb-1" />
          <div className="h-5 w-24 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  </div>
);

const EstimateList: React.FC<EstimateListProps> = ({
  estimates,
  onSelect,
  onEdit,
  onSubmitForReview,
  onApprove,
  onReject,
  onCreateVersion,
  onDelete,
  isLoading = false,
  emptyMessage = 'No estimates found',
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <LoadingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (estimates.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <FileSpreadsheet className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Estimates</h3>
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {estimates.map(estimate => (
        <EstimateCard
          key={estimate._id}
          estimate={estimate}
          onSelect={onSelect}
          onEdit={onEdit}
          onSubmitForReview={onSubmitForReview}
          onApprove={onApprove}
          onReject={onReject}
          onCreateVersion={onCreateVersion}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default EstimateList;
